import React, { useState, createContext, useContext, useEffect } from "react";
import { COMPACT_DESKTOP_MAX_PX } from "../constants/layoutBreakpoints";

const SideBarContext = createContext({
  isOpen: true,
  toggleSidebar: () => { },
  activePage: "",
  setActivePage: () => { }
});

export function useSideBarContext() {
  return useContext(SideBarContext);
}

const SIDEBAR_KEY = "fk.sidebarOpen";

function readSidebarPref() {
  try {
    const v = window.localStorage.getItem(SIDEBAR_KEY);
    if (v === "true") return true;
    if (v === "false") return false;
  } catch (_) { /* localStorage unavailable */ }
  return null;
}

function getInitialSidebarOpen() {
  if (typeof window === "undefined") return true;
  // Below lg the sidebar is an overlay drawer — start closed there regardless
  // of a persisted desktop expand/collapse choice.
  if (window.innerWidth < 1024) return false;
  const pref = readSidebarPref();
  if (pref !== null) return pref; // user's explicit choice wins over viewport
  return window.innerWidth > COMPACT_DESKTOP_MAX_PX;
}

function SideBarProvider({ children }) {
  const [isOpen, setIsOpen] = useState(getInitialSidebarOpen);
  const [activePage, setActivePage] = useState("Dashboard");

  useEffect(() => {
    let prevWide = window.innerWidth > COMPACT_DESKTOP_MAX_PX;
    const onResize = () => {
      const wide = window.innerWidth > COMPACT_DESKTOP_MAX_PX;
      if (wide !== prevWide) {
        // Respect an explicit user choice — don't auto-toggle over it on resize.
        if (readSidebarPref() === null) setIsOpen(wide);
        prevWide = wide;
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function toggleSidebar() {
    setIsOpen(prevIsOpen => {
      const next = !prevIsOpen;
      try { window.localStorage.setItem(SIDEBAR_KEY, String(next)); } catch (_) { /* ignore */ }
      return next;
    });
  }

  return (
    <SideBarContext.Provider
      value={{ isOpen, toggleSidebar, activePage, setActivePage }}
    >
      {children}
    </SideBarContext.Provider>
  );
}

export default SideBarProvider;
